import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Radio, Input, notification, Segmented } from "antd";
import { List, SquaresFour } from "@phosphor-icons/react";
import { useMutation, useQueryClient } from "react-query";
import { editCompanyBranch } from "../../../service";
import { useBranchContext } from "../../../contexts/branch-context";
import { ThemeToggle } from "../../atoms/ThemeToggle";
import {
  SectionTitle,
  SettingItem,
  SettingLabel,
  SettingDescription,
} from "../../../styles/shared-components";
import styled from "styled-components";
import { colors, spacing } from "../../../styles/theme";

const SubSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${spacing.xl} 0;
  padding-bottom: ${spacing.sm};
  border-bottom: 1px solid ${colors.borderLight};
`;

const Section = styled.div`
  margin-bottom: ${spacing.xxl};
`;

export const AppearanceSettings: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBranch } = useBranchContext();

  const [viewType, setViewType] = useState<"LIST" | "GRID">("LIST");
  const [displayMode, setDisplayMode] = useState<"products" | "services">("products");
  const [mainButtonText, setMainButtonText] = useState("");
  const [secondaryButtonText, setSecondaryButtonText] = useState("");

  useEffect(() => {
    if (!currentBranch) return;
    setViewType((currentBranch.viewType as "LIST" | "GRID") ?? "LIST");
    const config = currentBranch?.config?.welcome_screen;
    const mode: "products" | "services" =
      config?.main_button?.navigate === "services" || config?.main_button?.navigate === "/services"
        ? "services"
        : "products";
    setDisplayMode(mode);
    setMainButtonText(
      config?.main_button?.text ||
        (mode === "products"
          ? t("appearanceSettings.defaultProductsMenu")
          : t("appearanceSettings.defaultBookService"))
    );
    setSecondaryButtonText(
      config?.additional_button?.text ||
        config?.additional_buttons?.[0]?.text ||
        (mode === "products"
          ? t("appearanceSettings.defaultBookService")
          : t("appearanceSettings.defaultProductsMenu"))
    );
  }, [currentBranch?.id, currentBranch?.viewType, currentBranch?.config]);

  const { mutateAsync, isLoading } = useMutation(editCompanyBranch, {
    onSuccess: () => {
      queryClient.invalidateQueries(["/company-branches"]);
      notification.success({ message: t("appearanceSettings.changesSaved") });
    },
    onError: () => {
      notification.error({ message: t("appearanceSettings.saveError") });
    },
  });

  const handleViewTypeChange = (value: "LIST" | "GRID") => {
    setViewType(value);
    if (!currentBranch) return;
    mutateAsync({ id: currentBranch.id, data: { viewType: value } });
  };

  const handleSaveWelcomeScreen = (mode: "products" | "services", mainText: string, secondaryText: string) => {
    if (!currentBranch) return;
    const newConfig = { ...(currentBranch.config || {}) };
    newConfig.welcome_screen = {
      main_button: {
        text:
          mainText ||
          (mode === "products"
            ? t("appearanceSettings.defaultProductsMenu")
            : t("appearanceSettings.defaultBookService")),
        navigate: mode === "products" ? "dishes" : "services",
      },
      additional_button: {
        text:
          secondaryText ||
          (mode === "products"
            ? t("appearanceSettings.defaultBookService")
            : t("appearanceSettings.defaultProductsMenu")),
        navigate: mode === "products" ? "services" : "dishes",
      },
    };
    mutateAsync({ id: currentBranch.id, data: { config: newConfig } });
  };

  return (
    <>
      <Section>
        <SubSectionTitle>{t("appearanceSettings.adminPanel")}</SubSectionTitle>
        <SettingItem>
          <SettingLabel>{t("appearanceSettings.theme")}</SettingLabel>
          <SettingDescription style={{ marginBottom: spacing.md }}>
            {t("appearanceSettings.themeDescription")}
          </SettingDescription>
          <ThemeToggle showLabel={false} />
        </SettingItem>
      </Section>

      <Section>
        <SubSectionTitle>{t("appearanceSettings.clientApp")}</SubSectionTitle>

        <SettingItem>
          <SettingLabel>{t("appearanceSettings.menuItemsView")}</SettingLabel>
          <SettingDescription style={{ marginBottom: spacing.md }}>
            {t("appearanceSettings.menuItemsViewDescription")}
          </SettingDescription>
          <Segmented
            value={viewType}
            onChange={(v) => handleViewTypeChange(v as "LIST" | "GRID")}
            disabled={isLoading}
            options={[
              { label: <Flex align="center" gap={6}><List size={16} />{t("appearanceSettings.list")}</Flex>, value: "LIST" },
              { label: <Flex align="center" gap={6}><SquaresFour size={16} />{t("appearanceSettings.grid")}</Flex>, value: "GRID" },
            ]}
          />
        </SettingItem>

        <SettingItem>
          <SettingLabel>{t("appearanceSettings.mainSalesType")}</SettingLabel>
          <SettingDescription style={{ marginBottom: spacing.md }}>
            {t("appearanceSettings.mainSalesTypeDescription")}
          </SettingDescription>
          <Radio.Group
            value={displayMode}
            disabled={isLoading}
            onChange={(e) => {
              const value = e.target.value;
              setDisplayMode(value);
              handleSaveWelcomeScreen(value, mainButtonText, secondaryButtonText);
            }}
          >
            <Radio value="products">{t("appearanceSettings.productSales")}</Radio>
            <Radio value="services">{t("appearanceSettings.serviceSales")}</Radio>
          </Radio.Group>

          <div style={{ marginTop: 24, maxWidth: 400 }}>
            <SettingLabel>{t("appearanceSettings.mainButtonLabel")}</SettingLabel>
            <Input
              value={mainButtonText}
              placeholder={displayMode === "products" ? t("appearanceSettings.mainButtonPlaceholderProducts") : t("appearanceSettings.mainButtonPlaceholderServices")}
              onChange={(e) => setMainButtonText(e.target.value)}
              onBlur={() => handleSaveWelcomeScreen(displayMode, mainButtonText, secondaryButtonText)}
              style={{ marginTop: 8 }}
            />
          </div>

          <div style={{ marginTop: 16, maxWidth: 400 }}>
            <SettingLabel>{t("appearanceSettings.secondaryButtonLabel")}</SettingLabel>
            <Input
              value={secondaryButtonText}
              placeholder={displayMode === "products" ? t("appearanceSettings.secondaryButtonPlaceholderProducts") : t("appearanceSettings.secondaryButtonPlaceholderServices")}
              onChange={(e) => setSecondaryButtonText(e.target.value)}
              onBlur={() => handleSaveWelcomeScreen(displayMode, mainButtonText, secondaryButtonText)}
              style={{ marginTop: 8 }}
            />
          </div>
        </SettingItem>
      </Section>
    </>
  );
};
